'use client';

import React from 'react';
import { Modal, Button, Space, Typography, Progress } from 'antd';
import { ClockCircleOutlined, LogoutOutlined } from '@ant-design/icons';
import { useSession } from '@/shared/hooks/session/useSession';
import styles from './security.module.css';

const { Text, Title } = Typography;

/**
 * Session Timeout Warning Modal
 *
 * Non-dismissible warning near idle timeout with countdown + next actions.
 * SOC 2 CC6.1, CC6.3. Touch-friendly Continue / Sign Out controls.
 */
export function SessionTimeoutWarning() {
  const { idleWarningVisible, idleSecondsRemaining, dismissIdleWarning, logout } = useSession();

  const minutes = Math.floor(idleSecondsRemaining / 60);
  const seconds = idleSecondsRemaining % 60;
  const formattedTime = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  const totalWarningSeconds = 120;
  const progressPercent = Math.max(
    0,
    Math.round((idleSecondsRemaining / totalWarningSeconds) * 100)
  );
  const urgent = idleSecondsRemaining <= 30;

  return (
    <Modal
      open={idleWarningVisible}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      footer={null}
      centered
      width={420}
      styles={{
        body: { textAlign: 'center', padding: 24 },
      }}
    >
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        <ClockCircleOutlined
          style={{
            fontSize: 48,
            color: urgent ? '#ff4d4f' : '#faad14',
          }}
          aria-hidden
        />

        <Title level={4} style={{ margin: 0 }}>
          Session Timeout Warning
        </Title>

        <Text type="secondary">Your session will expire due to inactivity.</Text>

        <div
          className={styles.sessionCountdown}
          style={{ color: urgent ? '#ff4d4f' : undefined }}
          aria-live="polite"
          aria-atomic="true"
        >
          {formattedTime}
        </div>

        <Progress
          percent={progressPercent}
          showInfo={false}
          strokeColor={urgent ? '#ff4d4f' : '#faad14'}
          size="small"
          aria-hidden
        />

        <div className={styles.sessionActions}>
          <Button
            type="primary"
            size="large"
            onClick={dismissIdleWarning}
            className={styles.sessionBtn}
          >
            Continue Working
          </Button>
          <Button
            size="large"
            danger
            icon={<LogoutOutlined />}
            onClick={logout}
            className={styles.sessionBtn}
          >
            Sign Out
          </Button>
        </div>

        <Text type="secondary" style={{ fontSize: 12 }}>
          For your security, sessions are automatically terminated after a period of inactivity.
        </Text>
      </Space>
    </Modal>
  );
}

export default SessionTimeoutWarning;
